/**
 * RoleGuard — renders children only if user has the required minimum role.
 * Shows access denied message if insufficient permissions.
 */

import { useRole } from '@/hooks/useRole';
import { Card } from '@/components/ui/Card';
import type { UserRole } from '@/types';

interface RoleGuardProps {
  minRole: UserRole;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function RoleGuard({ minRole, children, fallback }: RoleGuardProps) {
  const { hasMinRole } = useRole();

  if (!hasMinRole(minRole)) {
    return fallback || (
      <Card padding="lg" className="text-center py-16">
        <p className="text-slate-500 dark:text-slate-400">You don't have permission to access this page.</p>
        <p className="text-sm text-slate-400 mt-2">Required role: {minRole}. Contact your organization admin for access.</p>
      </Card>
    );
  }

  return <>{children}</>;
}
